import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { getAllActivityList } from "../../data/redux/activity/_activityAction";
import * as Yup from "yup";
import { useFormik } from "formik";
import SearchIcon from "../../../icons/SearchIcon";
import UploadIcon from "../../../icons/UploadIcon";
import FileUploadWhiteIcon from "../../../icons/FileUploadWhiteIcon";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/deleteModal";
import {
  addNonRealLogHour,
  getNonRealLogHourDetails,
  getNonRealRemainingLogHour,
  updateNonRealLogHour,
} from "../../data/redux/nonRealLogHour/_nonRealLogHourAction";
import {
  MAX_FILE_SIZE,
  MAX_FILES,
  MAX_TOTAL_SIZE,
} from "../../../constants/common";
import { formatDate } from "../../../constants/date";

interface ActivityModalProps {
  show: boolean;
  onHide: () => void;
  uuid: string | null;
  onSuccess?: () => void;
}

const initialValues = {
  type: "",
  task_description: "",
  hours: 0,
  minutes: 0,
  date: "",
  participation_level: "",
  receipt_images: null,
};

const validationSchema = Yup.object({
  type: Yup.string().trim().required("Activity Name is required"),
  task_description: Yup.string().required("Description is required"),
  hours: Yup.number()
    .typeError("Hours must be a number")
    .min(0, "Hours cannot be less than 0")
    .max(24, "Hours cannot be more than 24")
    .test(
      "hours-or-minutes",
      "Either hours or minutes must be greater than 0",
      function (value) {
        const { minutes } = this.parent; // access sibling field
        return (value ?? 0) > 0 || (minutes ?? 0) > 0;
      }
    ),

  minutes: Yup.number()
    .typeError("Minutes must be a number")
    .min(0, "Minutes cannot be less than 0")
    .max(60, "Minutes cannot be more than 60")
    .test(
      "hours-or-minutes",
      "Either hours or minutes must be greater than 0",
      function (value) {
        const { hours } = this.parent; // access sibling field
        return (value ?? 0) > 0 || (hours ?? 0) > 0;
      }
    ),
  // date: Yup.string().required("Please pick a date"),
  date: Yup.date()
    .required("Please pick a date")
    .max(new Date(), "Future dates are not allowed"),
  participation_level: Yup.string().required("Participation Level is required"),
  receipt_images: Yup.mixed().nullable(),
});

const NonRealLogHourModal: React.FC<ActivityModalProps> = ({
  show,
  onHide,
  uuid,
  onSuccess,
}) => {
  const dispatch: any = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [allActivities, setAllActivities] = useState<any[]>([]);
  const [receiptImages, setReceiptImages] = useState<any[]>([]);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedImageUuid, setSelectedImageUuid] = useState<string | null>(
    null
  );
  const [uploadError, setUploadError] = useState("");
  const [remainingLogHours, setRemainingLogHours] = useState<string | null>(
    null
  );

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filtered act` list
  const filteredActivities = allActivities.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Formik setup with validation schema
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,

    onSubmit: async (values) => {
      console.log("values ===> ", values);
      try {
        const formData = new FormData();

        formData.append("type", values?.type);
        formData.append("task_description", values?.task_description);
        formData.append("hours", values?.hours?.toString());
        formData.append("minutes", values?.minutes?.toString());
        formData.append("date", values?.date);
        formData.append("participation_level", values?.participation_level);

        if (receiptFiles?.length > 0) {
          receiptFiles.forEach((file) => {
            formData.append("receipt_images", file);
          });
        }

        if (uuid) {
          await dispatch(updateNonRealLogHour({ uuid, values: formData }));
        } else {
          await dispatch(addNonRealLogHour(formData));
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong!");
      }
      formik.resetForm();
      setReceiptFiles([]);
      setReceiptImages([]);
      setRemainingLogHours(null);
      onHide();
    },
  });

  // Fetch log-hour details when modal is shown and uuid is provided
  useEffect(() => {
    if (show) {
      fetchAllActivityList();

      if (uuid) {
        const fetchData = async () => {
          try {
            dispatch(getNonRealLogHourDetails({ uuid })).then(
              (response: any) => {
                const logHour = response?.payload?.data;
                console.log("logHour ===>", logHour);

                if (logHour) {
                  const formattedDate = logHour.date
                    ? formatDate(logHour.date)
                    : "";

                  const data: any = {
                    type: logHour?.type?.uuid || "",
                    task_description: logHour?.task_description || "",
                    hours: parseInt(logHour?.hours || "0", 10),
                    minutes: parseInt(logHour?.minutes || "0", 10),
                    date: formattedDate,
                    participation_level: logHour?.participation_level || "",
                    receipt_images: null,
                  };

                  formik.setValues(data);
                  setReceiptImages(logHour?.receipt_images || []);
                }
              }
            );
          } catch (error) {
            console.error("Failed to load log hour details:", error);
            toast.error("Failed to load log hour details");
          }
        };
        fetchData();
      }
    } else {
      formik.resetForm();
      setReceiptImages([]);
      setReceiptFiles([]);
      setRemainingLogHours(null);
    }
  }, [show, uuid, dispatch]);

  // get All Activity List
  const fetchAllActivityList = async () => {
    try {
      const response = await dispatch(getAllActivityList({}));
      const allProps = response?.payload?.data || [];
      setAllActivities(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all activity:", error);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle delete confirmation
  const handleDeleteImage = () => {
    if (selectedImageUuid) {
      setReceiptImages((prev) =>
        prev.filter((image) => image.uuid !== selectedImageUuid)
      );
      setSelectedImageUuid(null);
      if (receiptFiles?.length + receiptImages?.length - 1 <= MAX_FILES) {
        setUploadError("");
      }
    }
  };

  // Handle opening delete modal
  const handleOpenDeleteModal = (uuid: string) => {
    setSelectedImageUuid(uuid);
    setShowDeleteModal(true);
  };

  // Handle closing delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedImageUuid(null);
  };

  // Handle successful deletion
  const handleDeleteSuccess = () => {
    handleDeleteImage();
    setShowDeleteModal(false);
  };

  // Function to fetch remaining log hours
  const fetchRemainingLogHours = async (date: string) => {
    try {
      if (!date) {
        setRemainingLogHours(null);
        return;
      }
      const response = await dispatch(getNonRealRemainingLogHour({ date }));
      setRemainingLogHours(response?.payload?.data?.message);
    } catch (error) {
      console.error("Failed to fetch remaining log hours:", error);
      setRemainingLogHours("Error fetching remaining log hours.");
    }
  };

  const validateAndAddFiles = (newFiles: File[]) => {
    // Filter out invalid files first
    const validFiles = newFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File "${file.name}" exceeds 10MB limit.`);
        return false;
      }
      // if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      //   toast.warn(`File "${file.name}" is not an image or PDF. Only images and PDFs are allowed.`);
      //   return false;
      // }
      return true;
    });

    if (validFiles.length === 0) return;

    const currentTotalSize = receiptFiles.reduce(
      (acc, file) => acc + file.size,
      0
    );
    const newTotalSize = validFiles.reduce((acc, file) => acc + file.size, 0);
    const futureTotalSize = currentTotalSize + newTotalSize;

    const totalFilesCount =
      receiptFiles.length + receiptImages.length + validFiles.length;

    // Check total file count
    if (totalFilesCount > MAX_FILES) {
      toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
      return;
    }

    // Check total size limit
    if (futureTotalSize > MAX_TOTAL_SIZE) {
      toast.error("Total upload size cannot exceed 50MB.");
      return;
    }

    // All checks passed
    setReceiptFiles((prev) => [...prev, ...validFiles]);
    setUploadError("");
    toast.success(`${validFiles.length} file(s) added successfully.`);
  };

  return (
    <>
      <Modal
        className="nonrealactivitiesmodal"
        show={show}
        onHide={onHide}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Log Hours</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form onSubmit={formik.handleSubmit}>
            {/* Activity Type */}
            <div
              className="col-12 col-sm-12 col-md-12 col-lg-12 mb-3"
              ref={dropdownRef}
            >
              <label className="form-label">Select Activity</label>
              <div className="dropdown ">
                <button
                  type="button"
                  className="form-select d-flex justify-content-between align-items-center bg-light form-control commonInput  "
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {allActivities?.find((p) => p?.uuid === formik?.values?.type)
                    ?.name || "Select an Activity"}
                  <i
                    className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                  />
                </button>

                {isDropdownOpen && (
                  <div
                    className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light add-scroll"
                    style={{ maxHeight: "initial" }}
                  >
                    <div className="input-group mb-3 commonInput bg-light bg-white">
                      <span className="input-group-text bg-white border-end-0 ">
                        <SearchIcon />
                      </span>
                      <input
                        type="text"
                        placeholder="Search activity by name"
                        className="name form-control border-start-0"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                      />
                    </div>

                    {filteredActivities?.length > 0 ? (
                      filteredActivities?.map((activity) => (
                        <div
                          key={activity?.uuid}
                          className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput  bg-light"
                          onClick={() => {
                            formik.setFieldValue("type", activity.uuid);
                            setIsDropdownOpen(false);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <label className="form-check-label ms-2">
                            <strong>{activity?.name}</strong>
                          </label>
                          <input
                            type="radio"
                            className="form-check-input mt-1"
                            name="propertyRadio"
                            checked={formik?.values?.type === activity?.uuid}
                            readOnly
                          />
                        </div>
                      ))
                    ) : (
                      <div className="text-muted text-center">
                        No results found.
                      </div>
                    )}
                  </div>
                )}
              </div>
              {formik.touched.type && formik.errors.type && (
                <div className="text-danger">{formik.errors.type}</div>
              )}
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                id="task_description"
                name="task_description"
                rows={3}
                className="form-control commonInput bg-light"
                placeholder="Describe your activity in detail..."
                value={formik.values.task_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.task_description &&
                formik.errors.task_description && (
                  <div className="text-danger">
                    {formik.errors.task_description}
                  </div>
                )}
            </div>

            {/* Duration */}
            <div className="mb-3">
              <div className="d-flex gap-3 mb-3">
                <div className="w-100">
                  <label className="form-label">Duration Hours</label>
                  <input
                    type="number"
                    name="hours"
                    className="form-control commonInput bg-light"
                    min={0}
                    max={24}
                    value={formik.values.hours}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.hours && formik.errors.hours && (
                    <div className="text-danger">{formik.errors.hours}</div>
                  )}
                </div>

                <div className="w-100">
                  <label className="form-label d-none d-md-block">
                    Duration Minutes
                  </label>
                  <input
                    type="number"
                    name="minutes"
                    className="form-control commonInput bg-light"
                    min={0}
                    max={60}
                    value={formik.values.minutes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.minutes && formik.errors.minutes && (
                    <div className="text-danger">{formik.errors.minutes}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Display Remaining Log Hours */}
            {remainingLogHours && (
              <div className="mb-3">
                <p className="text-muted">{remainingLogHours}</p>
              </div>
            )}

            {/* Date */}
            <div className="mb-3">
              <label className="form-label"> Date</label>
              <input
                type="date"
                name="date"
                className="form-control commonInput bg-light"
                value={formik.values.date}
                // onChange={formik.handleChange}
                max={new Date().toISOString().split("T")[0]} // Set max to today's date
                onChange={(e) => {
                  formik.handleChange(e); // Update formik value
                  // Fetch remaining log hours if property is selected
                  if (e.target.value) {
                    fetchRemainingLogHours(e.target.value);
                  } else {
                    setRemainingLogHours(null); // Clear message if no property or date
                  }
                }}
              />
              {formik.touched.date && formik.errors.date && (
                <div className="text-danger">{formik.errors.date}</div>
              )}
            </div>

            {/* Participation Type */}
            <div className="mb-3">
              <div className="user-type-box mb-4  rounded ">
                <label className="form-label">Participation Type</label>
                <div className="btn-radio d-flex flex-wrap ">
                  <div className="me-3 mb-2">
                    <input
                      type="radio"
                      id="active"
                      name="participation_level"
                      value="active"
                      checked={
                        formik?.values?.participation_level?.toLowerCase() ===
                        "active"
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <label htmlFor="spouse" className="radio-label">
                      Active
                    </label>
                  </div>

                  <div className="me-3 mb-2">
                    <input
                      type="radio"
                      id="passive"
                      name="participation_level"
                      value="passive"
                      checked={
                        formik?.values?.participation_level?.toLowerCase() ===
                        "passive"
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <label htmlFor="taxPro" className="radio-label">
                      Passive
                    </label>
                  </div>
                </div>
              </div>
              {formik.touched.participation_level &&
                formik.errors.participation_level && (
                  <div className="invalid-feedback d-block">
                    {formik.errors.participation_level}
                  </div>
                )}
            </div>

            {/* Supporting Documentation  */}
            <div className="mb-2">
              {/* Upload Header */}
              <div className="Upload-Photo d-flex align-items-center gap-3 mb-2">
                <UploadIcon />
                <h5 className="mb-0">Supporting Documentation</h5>
              </div>

              {/* Upload Box */}
              <div className="upload-section">
                <div
                  className="upload-area rounded text-center p-5 bg-white"
                  style={{
                    borderStyle: "dashed",
                    borderWidth: "2px",
                    borderColor: "#dee2e6",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    document.getElementById("receiptUploadInput")?.click()
                  }
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files) {
                      const newFiles = Array.from(e.dataTransfer.files);
                      validateAndAddFiles(newFiles);
                    }
                  }}
                >
                  <FileUploadWhiteIcon />
                  <p className="mb-0 text-muted">
                    Click or drag file to this area to upload
                  </p>

                  <input
                    type="file"
                    id="receiptUploadInput"
                    hidden
                    multiple
                    // accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        const newFiles = Array.from(e.target.files);
                        validateAndAddFiles(newFiles);
                        e.target.value = ""; // Reset input
                      }
                    }}
                  />
                </div>

                {/* Inline error */}
                {uploadError && (
                  <div className="text-danger mt-2">{uploadError}</div>
                )}

                {/* Show uploaded files and API receipt images */}
                {(receiptFiles?.length > 0 || receiptImages?.length > 0) && (
                  <div className="mt-3 uploaded-files-section">
                    <div className="mb-3 p-2 bg-light rounded border">
                      <small className="text-muted fw-semibold">
                        Uploaded: {receiptFiles.length + receiptImages.length} /{" "}
                        {MAX_FILES} files | Size:{" "}
                        {(
                          receiptFiles.reduce((acc, f) => acc + f.size, 0) /
                          (1024 * 1024)
                        ).toFixed(2)}{" "}
                        MB / 50 MB
                      </small>
                      {receiptFiles.reduce((acc, f) => acc + f.size, 0) >
                        MAX_TOTAL_SIZE * 0.9 && (
                        <span className="text-warning ms-2">
                          Warning: Near limit!
                        </span>
                      )}
                    </div>

                    <div className="mt-2 text-start">
                      {receiptImages.map((image, idx) => (
                        <div
                          key={image?.uuid}
                          className="d-flex justify-content-between align-items-center mb-1"
                        >
                          <a
                            href={image?.image}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {image?.image.split("/").pop()}
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleOpenDeleteModal(image.uuid)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}

                      {receiptFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center mb-1"
                        >
                          <span>{file?.name}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              setReceiptFiles((prev) => {
                                const updated = prev.filter(
                                  (_, i) => i !== idx
                                );
                                if (
                                  updated.length + receiptImages.length <=
                                  MAX_FILES
                                ) {
                                  setUploadError("");
                                }
                                return updated;
                              });
                            }}
                          >
                            <i className="fas fa-times cross-btn"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Button
              className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
              style={{
                backgroundColor: "#379fff",
                borderColor: "#379fff",
                width: "100%",
                fontWeight: 600,
              }}
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {uuid ? "Update Log Hours" : "Save Log Hours"}
            </Button>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type="deleteNonRealLogHourDocument"
        uuid={selectedImageUuid}
        data={uuid}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
};

export default NonRealLogHourModal;
